import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Scale } from "lucide-react";
import ConsumerHeader from "./ConsumerHeader";
import Footer from "./Footer";
import SEO from "./SEO";
import "./AudienceSplitter.css";
import { useTheme } from "../context/ThemeContext";
import {
  AUDIENCES as CHOICES,
  readAudience,
  setAudience,
  clearAudience,
} from "../utils/audience";

export default function AudienceSplitter() {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [savedChoice, setSavedChoice] = useState(null);

  useEffect(() => {
    setSavedChoice(readAudience());
  }, []);

  const choose = (key) => {
    setAudience(key);
    navigate(CHOICES[key].path);
  };

  // Link handlers keep real hrefs (middle-click, crawlers) but record the
  // audience first, or the splitter would bounce the visitor back next time.
  const goToDirectory = (e) => {
    e.preventDefault();
    setAudience("attorney");
    navigate("/directory");
  };

  const askQuestion = (e) => {
    e.preventDefault();
    choose("consumer");
  };

  const clearChoice = (e) => {
    e.preventDefault();
    clearAudience();
    setSavedChoice(null);
  };

  // Matches the lawfusionai.com outreach page, so a visitor arriving from an
  // email lands on the same light, card-based look.
  const cardStyle = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: "18px",
    background: darkMode ? "#1e293b" : "#fff",
    border: `1px solid ${darkMode ? "rgba(255,255,255,0.1)" : "#e2e8f0"}`,
    borderRadius: "18px",
    padding: "30px",
    boxShadow: darkMode
      ? "none"
      : "0 1px 2px rgba(15,23,42,0.04), 0 12px 32px -12px rgba(15,23,42,0.10)",
  };

  const iconTileStyle = (tone) => ({
    width: "46px",
    height: "46px",
    borderRadius: "12px",
    display: "grid",
    placeItems: "center",
    marginBottom: "16px",
    ...(tone === "blue"
      ? {
          background: darkMode ? "rgba(37,99,235,0.15)" : "#eff6ff",
          border: `1px solid ${darkMode ? "rgba(96,165,250,0.3)" : "#dbeafe"}`,
          color: darkMode ? "#93c5fd" : "#1d4ed8",
        }
      : {
          background: darkMode ? "rgba(124,58,237,0.15)" : "#faf5ff",
          border: `1px solid ${darkMode ? "rgba(167,139,250,0.3)" : "#e9d5ff"}`,
          color: darkMode ? "#c4b5fd" : "#6d28d9",
        }),
  });

  const headingStyle = {
    margin: "0 0 8px",
    fontSize: "21px",
    fontWeight: 800,
    letterSpacing: "-0.5px",
    color: darkMode ? "#f8fafc" : "#0f172a",
  };

  const bodyStyle = {
    margin: 0,
    fontSize: "15px",
    lineHeight: 1.55,
    color: darkMode ? "#94a3b8" : "#475569",
    textWrap: "pretty",
  };

  return (
    <>
      <SEO
        path="/"
        title="LawFusion — How can we help?"
        description="Choose whether you are a legal professional or have a legal question."
      />
      <ConsumerHeader minimal />
      <main
        style={{
          minHeight: "70vh",
          background: darkMode ? "#0f172a" : "#f8fafc",
          padding: "24px 20px 72px",
        }}
      >
        <div style={{ maxWidth: "860px", margin: "0 auto" }}>
          <section className="sp-hero">
            <svg
              className="sp-hero-waves"
              viewBox="0 0 860 300"
              preserveAspectRatio="none"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M-20 210 C 160 150, 300 300, 520 250 S 800 170, 880 110"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.2"
              />
              <path
                d="M-20 250 C 200 200, 340 320, 560 280 S 820 220, 880 180"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
              />
            </svg>
            <h1 className="sp-hero-title">
              Compare legal answers from multiple AIs —{" "}
              <span className="sp-hero-free">free</span>
            </h1>
            <p className="sp-hero-sub">Connect with attorneys</p>
            <img
              className="sp-hero-logos"
              src="/ai_logos_landing_page_tight.jpg"
              width="1720"
              height="574"
              alt="ChatGPT, Claude, Gemini, Grok, Perplexity"
            />
          </section>

          <h2
            style={{
              textAlign: "center",
              fontSize: "clamp(32px, 4.4vw, 46px)",
              lineHeight: 1.08,
              fontWeight: 800,
              letterSpacing: "-1.5px",
              margin: "0 0 36px",
              color: darkMode ? "#f8fafc" : "#0f172a",
            }}
          >
            How can LawFusion help you?
          </h2>

          <div className="splitter-grid">
            {/* DOM order is attorney-then-consumer, which is the desktop
                reading order. On one column the consumer card is pulled up
                first — see .splitter-grid in App.css. */}
            <div className="splitter-card splitter-card-attorney" style={cardStyle}>
              <div>
                <div style={iconTileStyle("blue")} aria-hidden="true">
                  <Scale size={24} />
                </div>
                <h2 style={headingStyle}>I'm an Attorney or Paralegal</h2>
                <p style={bodyStyle}>
                  Research legal issues, stress-test arguments, and compare
                  multiple AI perspectives. Join our free referral directory.
                </p>
              </div>
              {/* Attorneys land on the directory sign-up, the main thing the
                  site is recruiting them for. */}
              <button
                type="button"
                className="splitter-btn"
                onClick={goToDirectory}
              >
                Continue as Attorney/Paralegal
              </button>
            </div>

            <div className="splitter-card splitter-card-consumer" style={cardStyle}>
              <div>
                <div
                  style={{
                    ...iconTileStyle("violet"),
                    fontSize: "22px",
                    fontWeight: 800,
                    lineHeight: 1,
                  }}
                  aria-hidden="true"
                >
                  ?
                </div>
                <h2 style={headingStyle}>I Have a Legal Question</h2>
                <p
                  style={{
                    margin: "0 0 8px",
                    fontSize: "15px",
                    fontWeight: 700,
                    fontStyle: "italic",
                    color: darkMode ? "#cbd5e1" : "#475569",
                  }}
                >
                  For Non-Attorneys
                </p>
                <p style={bodyStyle}>
                  Compare answers from multiple AIs for free, and connect with
                  an attorney if you need one.
                </p>
                <p
                  style={{
                    margin: "8px 0 0",
                    fontSize: "14px",
                    lineHeight: 1.5,
                    fontStyle: "italic",
                    color: darkMode ? "#64748b" : "#94a3b8",
                  }}
                >
                  General legal information, not legal advice.
                </p>
              </div>
              <button
                type="button"
                className="splitter-btn"
                onClick={() => choose("consumer")}
              >
                Continue with My Question
              </button>
            </div>
          </div>

          {savedChoice && (
            <div
              style={{
                marginTop: "26px",
                textAlign: "center",
                fontSize: "13.5px",
                color: darkMode ? "#94a3b8" : "#64748b",
              }}
            >
              Remembered choice:{" "}
              <strong style={{ color: darkMode ? "#e2e8f0" : "#1e293b" }}>
                {CHOICES[savedChoice].label}
              </strong>{" "}
              ·{" "}
              <a href="#choose" onClick={clearChoice} style={{ color: "#1d4ed8" }}>
                choose again
              </a>
            </div>
          )}

          {/* The attorney pitch from the lawfusionai.com outreach page. */}
          <div className="sp-pitch">
            <section>
              <h2 className="sp-pitch-h2">How LawFusion Works</h2>
              <p className="sp-pitch-lead">
                Here's what people searching for legal help are already doing —
                before they ever call a firm.
              </p>
              <p className="sp-pitch-aside">
                Attorneys — keep reading. Not an attorney? Have a legal
                question?{" "}
                <a href="/ask" className="sp-pitch-link" onClick={askQuestion}>
                  Ask here &rarr;
                </a>
              </p>
            </section>

            <section className="sp-pitch-card sp-pitch-card--first">
              <h3 className="sp-pitch-label">Traditional online legal Q&amp;A</h3>
              <p className="sp-pitch-text">
                Create an account &rarr; ask a question &rarr; wait for an
                attorney response.
              </p>
              <p className="sp-pitch-note">
                (Avvo says users are usually notified within 12 hours.)
              </p>
            </section>

            <section className="sp-pitch-card sp-pitch-card--blue">
              <h3 className="sp-pitch-h3">LawFusion works differently.</h3>
              <p className="sp-pitch-text">
                Potential clients can ask their legal question once and
                immediately compare answers from ChatGPT, Claude, Gemini, Grok,
                and Perplexity — side by side.
              </p>
              <ul className="sp-pitch-list">
                {[
                  ["No app-switching:", "No opening five different tools or pasting the same question over and over."],
                  ["Instant cross-checking:", "See where the AIs agree, where they differ, and what one may have caught that another missed."],
                  ["One-click summary:", "Pull the key points together automatically."],
                  ["Human expertise when needed:", "Relevant attorney profiles can appear alongside the AI results while the user is already researching the issue."],
                ].map(([lead, rest]) => (
                  <li key={lead}>
                    <span className="sp-pitch-bullet" aria-hidden="true">•</span>
                    <span>
                      <strong>{lead}</strong> {rest}
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="sp-pitch-diff">
              <h2 className="sp-pitch-diff-title">The Difference</h2>
              <div className="sp-pitch-compare">
                <div className="sp-pitch-card">
                  <h3 className="sp-pitch-label">Traditional Q&amp;A</h3>
                  <p className="sp-pitch-text">
                    Create an account &rarr; ask &rarr; wait.
                  </p>
                </div>
                <div className="sp-pitch-card sp-pitch-card--tint">
                  <h3 className="sp-pitch-label">LawFusion</h3>
                  <p className="sp-pitch-text">
                    Ask once &rarr; compare 5 AI perspectives &rarr; get key
                    points &rarr; connect with an attorney if needed.
                  </p>
                </div>
              </div>
            </section>

            <section className="sp-pitch-why">
              <h2 className="sp-pitch-why-title">Why This Matters for Attorneys</h2>
              <p className="sp-pitch-why-text">
                That's the moment someone decides who to call. If your profile
                isn't there, someone else's is.
              </p>
              <p className="sp-pitch-why-text">
                Your profile can appear in front of people already researching
                the exact type of case you handle — right as they're deciding
                whether they need an attorney.
              </p>
              <a
                href="/directory"
                className="sp-pitch-btn-primary"
                onClick={goToDirectory}
              >
                Join the LawFusion Referral Directory - Free
              </a>
            </section>

            <a
              href="/ask"
              className="sp-pitch-btn-secondary"
              onClick={askQuestion}
            >
              Not an Attorney? Ask a Legal Question &rarr;
            </a>
          </div>
        </div>
      </main>
      {/* The splitter is itself the audience switch, so it omits the link. */}
      <Footer audience="consumer" showAudienceSwitch={false} />
    </>
  );
}
